import { useEffect } from 'react';

// Scroll only the modal body: scrollIntoView can pan Safari's locked page and
// move the sheet itself underneath the keyboard.
export function revealModalField(body, viewport) {
  const field = body?.ownerDocument.activeElement;
  if (!field || !body.contains(field)
    || !field.matches('input, textarea, [contenteditable="true"]')) return;

  const bounds = body.getBoundingClientRect();
  const rect = field.getBoundingClientRect();
  const top = Math.max(bounds.top, viewport?.offsetTop ?? 0) + 12;
  const bottom = Math.min(bounds.bottom, viewport
    ? viewport.offsetTop + viewport.height : bounds.bottom) - 12;
  if (bottom <= top) return;

  // For a textarea taller than the available space, keep its top visible and
  // let the textarea's own scrolling follow the caret.
  const delta = rect.top < top || rect.height > bottom - top
    ? rect.top - top
    : Math.max(0, rect.bottom - bottom);
  if (delta) body.scrollTop += delta;
}

export default function useModalFocusVisibility(scrollRef, enabled) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const body = scrollRef.current?.getScrollableNode?.();
    if (!body?.addEventListener) return undefined;
    const viewport = window.visualViewport;
    let frame;
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => revealModalField(body, viewport));
    };
    // Wait for the modal shell to resize before measuring its scrollable body.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(body);
    body.addEventListener('focusin', schedule);
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      body.removeEventListener('focusin', schedule);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [scrollRef, enabled]);
}
